import { toNum } from '@/utils/numeric';
import type { Numeric } from '@/utils/numeric';

/**
 * What an installed irrigation system draws, derived from the emitters in it.
 *
 * A deliberate mirror of `irrigationInstallService.computeInstallFlow` on the
 * server, which stays authoritative: every read of a saved spec returns the
 * server's figures and the panel displays those. This copy exists only so the
 * numbers move while somebody is still typing — a form that reveals its answer
 * one round trip after each keystroke is a form nobody trusts enough to finish.
 *
 * The two must agree, so the same worked example is pinned in both test suites
 * (2 L/h emitters at 30 cm on 4 m rows over 2 ha = 555.57 L/min). If this is
 * changed, change `irrigationInstallService.js` with it.
 */

export type CountingMethod = 'grid' | 'per_plant' | 'rate_per_ha';

export interface IrrigationSystemType {
  key: string;
  display_name: string;
  counting_method: CountingMethod;
  wets_full_surface: boolean;
  default_emitter_flow_lph: Numeric;
  sort_order: number;
  notes: string | null;
}

/** The stored spec, and equally the shape the form drafts into. */
export interface InstallSpecFields {
  system_type_key: string;
  emitter_flow_lph: Numeric;
  emitter_model: string | null;
  emitter_spacing_m: Numeric;
  lateral_spacing_m: Numeric;
  laterals_per_row: Numeric;
  emitters_per_plant: Numeric;
  flow_lph_per_ha: Numeric;
  design_pressure_kpa: Numeric;
  installed_year: number | null;
  notes: string | null;
}

export interface InstallSpec extends InstallSpecFields {
  id: number;
  cultivar_area_id: number;
  created_at: string;
  updated_at: string;
}

/** Only the three cultivar-area columns the arithmetic reads. */
export interface AreaGeometry {
  area_hectares: Numeric;
  tree_count: number | null;
  row_spacing_meters: Numeric;
}

export interface FlowWarning {
  level: 'info' | 'warn' | 'error';
  code: string;
  message: string;
}

export interface DerivedFlow {
  counting_method: CountingMethod;
  wets_full_surface: boolean;
  area_hectares: number | null;
  emitters_per_m2: number | null;
  emitter_count: number | null;
  lateral_spacing_m: number | null;
  lateral_spacing_source: 'stated' | 'row_spacing' | null;
  flow_lph: number | null;
  flow_lpm: number | null;
  lph_per_ha: number | null;
  application_rate_mm_h: number | null;
  complete: boolean;
  warnings: FlowWarning[];
}

/** Positive-or-null: a spacing or a flow of 0 is absent, not a valid input. */
function pos(v: Numeric | undefined): number | null {
  const n = toNum(v);
  return n !== null && n > 0 ? n : null;
}

function round(n: number | null, digits: number): number | null {
  if (n === null || !Number.isFinite(n)) return null;
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}

export function computeInstallFlow(
  spec: Partial<InstallSpecFields>,
  systemType: Pick<IrrigationSystemType, 'counting_method' | 'wets_full_surface'>,
  area: AreaGeometry,
): DerivedFlow {
  const warnings: FlowWarning[] = [];
  const method = systemType.counting_method;

  const areaHa = pos(area.area_hectares);
  const areaM2 = areaHa === null ? null : areaHa * 10000;
  if (areaHa === null) {
    warnings.push({
      level: 'warn', code: 'NO_AREA',
      message: 'This cultivar area has no hectares recorded, so no total can be worked out. Draw or enter its area under Farms.',
    });
  }

  const emitterFlowLph = pos(spec.emitter_flow_lph);

  let emittersPerM2: number | null = null;
  let emitterCount: number | null = null;
  let lateralSpacingM: number | null = null;
  let lateralSpacingSource: 'stated' | 'row_spacing' | null = null;
  let flowLph: number | null = null;

  if (method === 'grid') {
    const emitterSpacingM = pos(spec.emitter_spacing_m);
    const stated = pos(spec.lateral_spacing_m);
    const rowSpacing = pos(area.row_spacing_meters);
    const lateralsPerRow = pos(spec.laterals_per_row) ?? 1;

    if (stated !== null) {
      lateralSpacingM = stated;
      lateralSpacingSource = 'stated';
    } else if (rowSpacing !== null) {
      // The normal case for orchard drip: a line down every row, so the lateral
      // spacing is the row spacing — halved where two lines are run.
      lateralSpacingM = rowSpacing / lateralsPerRow;
      lateralSpacingSource = 'row_spacing';
    }

    if (emitterSpacingM === null) {
      warnings.push({
        level: 'warn', code: 'NO_EMITTER_SPACING',
        message: 'Emitter spacing along the line is needed to count the emitters.',
      });
    }
    if (lateralSpacingM === null) {
      warnings.push({
        level: 'warn', code: 'NO_LATERAL_SPACING',
        message: 'No lateral spacing stated, and this planting has no row spacing recorded to fall back on. Enter one or set the row spacing on the cultivar area.',
      });
    }
    if (emitterSpacingM !== null && lateralSpacingM !== null) {
      emittersPerM2 = 1 / (emitterSpacingM * lateralSpacingM);
      if (areaM2 !== null) emitterCount = Math.round(emittersPerM2 * areaM2);
    }
  } else if (method === 'per_plant') {
    const perPlant = pos(spec.emitters_per_plant);
    const treeCount = pos(area.tree_count);

    if (perPlant === null) {
      warnings.push({
        level: 'warn', code: 'NO_EMITTERS_PER_PLANT',
        message: 'Emitters per tree is needed to count the emitters.',
      });
    }
    if (treeCount === null) {
      warnings.push({
        level: 'warn', code: 'NO_TREE_COUNT',
        message: 'This cultivar area has no tree count recorded, so the emitters cannot be counted. Set it under Farms — it can be calculated from the spacings there.',
      });
    }
    if (perPlant !== null && treeCount !== null) {
      emitterCount = Math.round(perPlant * treeCount);
      if (areaM2 !== null) emittersPerM2 = emitterCount / areaM2;
    }
  }

  if (method === 'rate_per_ha') {
    const ratePerHa = pos(spec.flow_lph_per_ha);
    if (ratePerHa === null) {
      warnings.push({
        level: 'warn', code: 'NO_RATE',
        message: 'A delivery rate per hectare is needed for this kind of system.',
      });
    }
    if (ratePerHa !== null && areaHa !== null) flowLph = ratePerHa * areaHa;
  } else {
    if (emitterFlowLph === null) {
      warnings.push({
        level: 'warn', code: 'NO_EMITTER_FLOW',
        message: 'Emitter output in litres per hour is needed to work out a total.',
      });
    }
    // Multiplied by the ROUNDED count on purpose: the count is what the panel
    // shows, and a total that does not equal the numbers beside it reads as a
    // mistake in whichever of the two the reader trusts less.
    if (emitterCount !== null && emitterFlowLph !== null) {
      flowLph = emitterCount * emitterFlowLph;
    }
  }

  const flowLpm = flowLph === null ? null : flowLph / 60;
  const lphPerHa = flowLph !== null && areaHa !== null ? flowLph / areaHa : null;
  // 1 L spread over 1 m² is 1 mm deep, so L/h/m² IS mm/h. For a system that
  // does not wet the whole surface this is a block average rather than what
  // falls at the tree, which is why wets_full_surface travels with it.
  const applicationRateMmH = flowLph !== null && areaM2 !== null ? flowLph / areaM2 : null;

  return {
    counting_method: method,
    wets_full_surface: systemType.wets_full_surface === true,
    area_hectares: round(areaHa, 4),
    emitters_per_m2: round(emittersPerM2, 4),
    emitter_count: emitterCount,
    lateral_spacing_m: round(lateralSpacingM, 3),
    lateral_spacing_source: lateralSpacingSource,
    flow_lph: round(flowLph, 2),
    flow_lpm: round(flowLpm, 2),
    lph_per_ha: round(lphPerHa, 2),
    application_rate_mm_h: round(applicationRateMmH, 3),
    complete: flowLpm !== null,
    warnings,
  };
}

/**
 * Which fields a counting method actually reads.
 *
 * Drives both the form (what to ask for) and the save guard (what to refuse
 * without), so a type can never present a field it will then ignore.
 */
export function fieldsFor(method: CountingMethod): {
  emitterFlow: boolean;
  gridSpacing: boolean;
  perPlant: boolean;
  ratePerHa: boolean;
} {
  return {
    emitterFlow: method !== 'rate_per_ha',
    gridSpacing: method === 'grid',
    perPlant: method === 'per_plant',
    ratePerHa: method === 'rate_per_ha',
  };
}

/**
 * The one message a save would be refused for, or null when it would be taken.
 *
 * Mirrors `assertSpecUsable` on the server. Checked here too so the button can
 * say what is missing before the round trip, rather than after.
 */
export function missingRequiredField(
  spec: Partial<InstallSpecFields>,
  systemType: Pick<IrrigationSystemType, 'counting_method' | 'display_name'>,
): string | null {
  const f = fieldsFor(systemType.counting_method);
  if (f.ratePerHa && pos(spec.flow_lph_per_ha) === null) {
    return 'Enter the delivery rate in litres per hour per hectare.';
  }
  if (f.emitterFlow && pos(spec.emitter_flow_lph) === null) {
    return 'Enter the emitter output in litres per hour.';
  }
  if (f.gridSpacing && pos(spec.emitter_spacing_m) === null) {
    return 'Enter the emitter spacing along the line, in metres.';
  }
  if (f.perPlant && pos(spec.emitters_per_plant) === null) {
    return 'Enter the number of emitters at each tree.';
  }
  return null;
}
